import {AxiosConfig} from '../core';

export interface ServerSentEvent {
  event: string;
  data: string;
}

export function createSseParser(onEvent: (event: ServerSentEvent) => void) {
  let line = '';
  let skipLF = false;
  let firstCharacter = true;
  let event = '';
  let data: string[] = [];

  const processLine = () => {
    const current = line;
    line = '';
    if (current === '') {
      const message = data.length ? {event: event || 'message', data: data.join('\n')} : null;
      event = '';
      data = [];
      if (message) onEvent(message);
      return;
    }
    if (current.startsWith(':')) return;
    const colon = current.indexOf(':');
    const field = colon < 0 ? current : current.slice(0, colon);
    let value = colon < 0 ? '' : current.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    if (field === 'event') event = value;
    if (field === 'data') data.push(value);
  };

  return {
    push(chunk: string): void {
      for (let index = 0; index < chunk.length; index += 1) {
        const character = chunk[index];
        if (firstCharacter) {
          firstCharacter = false;
          if (character === '\uFEFF') continue;
        }
        if (skipLF) {
          skipLF = false;
          if (character === '\n') continue;
        }
        if (character === '\r') {
          skipLF = true;
          processLine();
        } else if (character === '\n') {
          processLine();
        } else {
          line += character;
        }
      }
    },
  };
}

export async function consumeNotificationStream(
  token: string,
  signal: AbortSignal,
  onEvent: (event: string) => void,
): Promise<void> {
  const checkAborted = () => {
    if (signal.aborted) throw new DOMException('Notification stream aborted', 'AbortError');
  };
  checkAborted();
  const baseURL = (AxiosConfig.baseURL || '/api/').replace(/\/+$/, '');
  const response = await fetch(`${baseURL}/v1/notifications/stream`, {
    method: 'GET',
    headers: {Authorization: `Bearer ${token}`, Accept: 'text/event-stream'},
    credentials: AxiosConfig.withCredentials ? 'include' : 'same-origin',
    signal,
  });
  const contentType = response.headers.get('content-type')?.split(';')[0].trim().toLowerCase();
  if (signal.aborted || !response.ok || contentType !== 'text/event-stream' || !response.body) {
    if (response.body) void response.body.cancel().catch(() => {});
    checkAborted();
    throw new Error(`Invalid notification stream response (${response.status})`);
  }

  const reader = response.body.getReader();
  const cancel = () => { void reader.cancel().catch(() => {}); };
  signal.addEventListener('abort', cancel);
  const decoder = new TextDecoder();
  const parser = createSseParser(({event}) => {
    if (!signal.aborted && (event === 'ready' || event === 'notifications-changed')) onEvent(event);
  });
  let completed = false;
  try {
    while (true) {
      checkAborted();
      const {done, value} = await reader.read();
      checkAborted();
      if (done) {
        parser.push(decoder.decode());
        completed = true;
        return;
      }
      parser.push(decoder.decode(value, {stream: true}));
    }
  } finally {
    signal.removeEventListener('abort', cancel);
    if (!completed) cancel();
    reader.releaseLock();
  }
}
